import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { canMediateApiAuthCanMediateGet } from '@/lib/api/generated';
import { useAuth } from '@/lib/auth/auth-context';
import { UserType } from '@/lib/auth/permissions';
import { useMyPermissions } from '@/lib/auth/my-permissions';
import { useGating } from '@/lib/auth/use-gating';
import { useLocalePrefix } from '@/lib/hooks/useLocaleNavigate';
import { NAV_TREE, NavNode, pruneNav, orderTopMenus, findActiveTrail, isNodeActive } from './navTree';

export interface NavState {
  /** The whole tree, pruned for this user (GEN-04-03). */
  tree: NavNode[];
  /** The top menus in GEN-04-02 order. */
  topMenus: NavNode[];
  /** Top menu → … → the node owning the current address (GEN-04-05). */
  trail: NavNode[];
  /** The open top menu, if any. */
  activeTop: NavNode | null;
  /** Exact-match test for one node against the current address. */
  isActive: (node: NavNode) => boolean;
}

/**
 * One nav computation for the header, the sidebar and the narrow-screen Menu
 * (GEN-04). Visibility reads the backend's resolved permissions via useGating
 * (ENG-YAKSON-080), with the legacy userType matrix as fallback while they load,
 * so the header never flashes empty.
 */
export function useNavTree(): NavState {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const prefix = useLocalePrefix();
  const { user } = useAuth();
  const { canFeature, canKey, canRoute } = useGating();
  const { isHq, managesLeave } = useMyPermissions();
  const userType = (user?.userType || 'RECEPTIONIST') as UserType;

  // Y-014: Mediation nav is HM-or-Admin only. Probe the server because HM
  // identity lives on Staff.role, not on session.userType.
  const [canMediate, setCanMediate] = useState(false);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await canMediateApiAuthCanMediateGet();
        if (!cancelled) setCanMediate(Boolean(data.canMediate));
      } catch {
        if (!cancelled) setCanMediate(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const tree = useMemo(
    () => pruneNav(NAV_TREE, { userType, canMediate, isHq, managesLeave, canFeature, canKey, canRoute }),
    [userType, canMediate, isHq, managesLeave, canFeature, canKey, canRoute],
  );

  const bare = pathname.startsWith(prefix) ? pathname.slice(prefix.length) || '/' : pathname;
  const currentTab = searchParams.get('tab');

  return useMemo(() => {
    const trail = findActiveTrail(tree, bare, currentTab);
    return {
      tree,
      topMenus: orderTopMenus(tree),
      trail,
      activeTop: trail[0] ?? null,
      isActive: (node: NavNode) => isNodeActive(node, bare, currentTab),
    };
  }, [tree, bare, currentTab]);
}
